import React, { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import Sidebar from "./components/Sidebar";
import Navbar from "./components/Navbar";
import {
  FiArrowLeft,
  FiChevronRight,
  FiCalendar,
  FiRefreshCw,
  FiAlertTriangle,
  FiExternalLink,
  FiCopy,
  FiCheck,
  FiRotateCcw,
  FiX,
} from "react-icons/fi";
import { getUserCoinHistory } from "../api/userCoinHistoryApi";
import { revertParticipantEventPoints, getEvents } from "../api/eventApi";
import {
  setLocalPointDeduction,
  getLocalPointDeductions,
} from "../utils/eventCoinUtils";
import toast, { Toaster } from "react-hot-toast";



const baseUrl = process.env.REACT_APP_API_BASE_URL;

const REVERT_REASON_OPTIONS = [
  "Bot / Automated Activity",
  "Referral Fraud / Fake Accounts",
  "Duplicate Account Exploitation",
  "Event Terms & Rules Violation",
  "Admin Manual Adjustment",
  "Other / Custom Reason",
];

const SOURCE_FILTERS = [
  { key: "all", label: "All Sources", icon: "🌐" },
  { key: "mining", label: "App Mining", icon: "⚡" },
  { key: "browser", label: "Zeta Browser", icon: "💻" },
  { key: "event", label: "Event Prizes", icon: "🏆" },
  { key: "event_points", label: "Event Points", icon: "🎯" },
  { key: "mini_games", label: "Mini Games", icon: "🎮" },
  { key: "ban", label: "Reversions", icon: "🛡️" },
];

const TYPE_FILTERS = [
  { key: "all", label: "All Types" },
  { key: "CREDIT", label: "+ Credits Only" },
  { key: "DEBIT", label: "- Debits Only" },
];

const getSourceBadgeStyle = (source) => {
  switch (source) {
    case "app_mining":
    case "mining":
      return "bg-emerald-50 text-emerald-700 border-emerald-200";
    case "zeta_browser":
    case "browser":
      return "bg-blue-50 text-blue-700 border-blue-200";
    case "event_prize":
    case "event":
      return "bg-amber-50 text-amber-800 border-amber-200";
    case "event_point":
    case "event_points":
    case "points":
      return "bg-purple-50 text-purple-700 border-purple-200";
    case "game":
    case "mini_games":
    case "boomcards":
    case "boom_cards":
    case "tap_game":
    case "tap":
    case "slots":
    case "slots_game":
    case "spinwheel":
    case "spin_wheel":
    case "spin":
      return "bg-indigo-50 text-indigo-700 border-indigo-200";
    case "game_2048":
    case "2048":
      return "bg-amber-50 text-amber-800 border-amber-200";
    case "fruit_bomb":
    case "fruit-bomb":
    case "fruit_Bomb":
      return "bg-rose-50 text-rose-700 border-rose-200";
    case "admin_reversion":
    case "reversion":
    case "reversions":
    case "point_reversion":
    case "event_reversion":
    case "ban":
      return "bg-rose-50 text-rose-700 border-rose-200";
    default:
      return "bg-gray-100 text-gray-700 border-gray-200";
  }
};


const getSourceIcon = (source) => {
  switch (source) {
    case "app_mining":
    case "mining":
      return "⚡";
    case "zeta_browser":
    case "browser":
      return "💻";
    case "event_prize":
    case "event":
      return "🏆";
    case "event_point":
    case "event_points":
    case "points":
      return "🎯";
    case "game":
    case "mini_games":
    case "boomcards":
    case "boom_cards":
    case "tap_game":
    case "tap":
    case "slots":
    case "slots_game":
    case "spinwheel":
    case "spin_wheel":
    case "spin":
      return "🎮";
    case "game_2048":
    case "2048":
      return "🧩";
    case "fruit_bomb":
    case "fruit-bomb":
    case "fruit_Bomb":
      return "🍉";
    case "admin_reversion":
    case "reversion":
    case "point_reversion":
    case "event_reversion":
    case "ban":
      return "🛡️";
    default:
      return "🪙";
  }
};

const formatCoinAmount = (amt) => {
  if (amt == null) return "0.0000";
  const num = Number(amt);
  return isNaN(num) ? String(amt) : num.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 4 });
};

const formatDateTime = (dateStr) => {
  if (!dateStr) return "—";
  try {
    const d = new Date(dateStr);
    return d.toLocaleString("en-US", {
      month: "short",
      day: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
      hour12: true,
    });
  } catch {
    return String(dateStr);
  }
};

export default function UserCoinHistoryPage() {
  const { id: routeUserId } = useParams();
  const navigate = useNavigate();

  const [userInfo, setUserInfo] = useState(null);

  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [history, setHistory] = useState([]);
  const [summary, setSummary] = useState({ currentPageCredited: 0, currentPageDebited: 0 });
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(20);
  const [sourceFilter, setSourceFilter] = useState("all");
  const [typeFilter, setTypeFilter] = useState("all");
  const [pagination, setPagination] = useState({ total: 0, totalPages: 1, page: 1, perPage: 20 });
  const [error, setError] = useState("");
  const [copied, setCopied] = useState(false);

  const [sourceTotals, setSourceTotals] = useState({
    appMining: 0,
    zetaBrowser: 0,
    eventPrizes: 0,
    eventPoints: 0,
  });

  // Revert Modal State
  const [revertModalOpen, setRevertModalOpen] = useState(false);
  const [revertAmount, setRevertAmount] = useState("");
  const [revertPreset, setRevertPreset] = useState("custom");
  const [revertReason, setRevertReason] = useState(REVERT_REASON_OPTIONS[0]);
  const [revertCustomNote, setRevertCustomNote] = useState("");
  const [revertLoading, setRevertLoading] = useState(false);
  const [eventsList, setEventsList] = useState([]);
  const [selectedEventId, setSelectedEventId] = useState("");

  const handleOpenRevertModal = async () => {
    try {
      const res = await getEvents({ page: 1, limit: 100 });
      const evts = res?.data?.events || (Array.isArray(res?.data) ? res.data : []);
      setEventsList(evts);
      if (evts.length > 0) {
        setSelectedEventId(evts[0].eventId || evts[0].id);
      }
    } catch {
      // ignore
    }
    const currentPts = sourceTotals.eventPoints || 0;
    setRevertAmount(currentPts > 0 ? String(Math.min(100, currentPts)) : "0");
    setRevertPreset("custom");
    setRevertReason(REVERT_REASON_OPTIONS[0]);
    setRevertCustomNote("");
    setRevertModalOpen(true);
  };

  const handleCloseRevertModal = () => {
    if (revertLoading) return;
    setRevertModalOpen(false);
  };

  const handlePresetSelect = (percentage) => {
    setRevertPreset(String(percentage));
    const currentPts = sourceTotals.eventPoints || 0;
    if (percentage === 100) {
      setRevertAmount(String(currentPts));
    } else {
      const calculated = Math.round((currentPts * percentage) / 100);
      setRevertAmount(String(calculated));
    }
  };

  const handleConfirmRevert = async (e) => {
    if (e) e.preventDefault();
    const numPoints = Number(revertAmount);
    if (isNaN(numPoints) || numPoints <= 0) {
      toast.error("Please enter a valid positive number of points to revert.");
      return;
    }

    const currentPts = sourceTotals.eventPoints || 0;
    if (currentPts > 0 && numPoints > currentPts) {
      toast.error(`Revert amount cannot exceed current event points (${currentPts.toLocaleString()} pts).`);
      return;
    }

    setRevertLoading(true);
    const finalReason = `${revertReason}${revertCustomNote.trim() ? ` - ${revertCustomNote.trim()}` : ""}`;
    try {
      await revertParticipantEventPoints({
        eventId: selectedEventId || "default_event",
        userId: routeUserId,
        pointsToRevert: numPoints,
        reason: finalReason,
      });

      setLocalPointDeduction(selectedEventId, routeUserId, numPoints, {
        reason: finalReason,
      });

      toast.success(`Successfully reverted ${numPoints.toLocaleString()} points for User #${routeUserId}!`);
      setRevertModalOpen(false);
      fetchHistory(true);
    } catch (err) {
      toast.error(err.message || "Failed to revert points");
    } finally {
      setRevertLoading(false);
    }
  };


  const computeSourceTotalsFromList = (list) => {
    let appMining = 0;
    let eventPrizes = 0;
    let eventPoints = 0;
    const browserTxs = [];

    (list || []).forEach((tx) => {
      const src = String(tx.source || "").toLowerCase().trim();
      const isDebit = String(tx.transactionType || "").toUpperCase() === "DEBIT";
      const amt = Number(tx.amount) || 0;
      const signedAmt = isDebit ? -amt : amt;

      if (src === "app_mining" || src === "mining") {
        appMining += signedAmt;
      } else if (src === "zeta_browser" || src === "browser") {
        browserTxs.push(tx);
      } else if (src === "event_prize" || src === "event_prizes" || src === "event") {
        eventPrizes += signedAmt;
      } else if (src === "event_point" || src === "event_points" || src === "points") {
        eventPoints += signedAmt;
      }
    });

    // Dynamically get true Zeta Browser total from the database ledger:
    // If the latest browser record contains balanceAfter snapshot from DB, use it;
    // otherwise sum all browser transaction amounts.
    let zetaBrowser = 0;
    if (browserTxs.length > 0) {
      const sortedBrowserTxs = [...browserTxs].sort((a, b) => {
        const dateA = new Date(a.transactionDate || a.createdAt || 0).getTime();
        const dateB = new Date(b.transactionDate || b.createdAt || 0).getTime();
        return dateB - dateA;
      });

      const latestBrowserTx = sortedBrowserTxs[0];
      if (
        latestBrowserTx &&
        latestBrowserTx.balanceAfter != null &&
        !isNaN(Number(latestBrowserTx.balanceAfter))
      ) {
        zetaBrowser = Number(latestBrowserTx.balanceAfter);
      } else {
        zetaBrowser = browserTxs.reduce((sum, tx) => {
          const isDebit = String(tx.transactionType || "").toUpperCase() === "DEBIT";
          const amt = Number(tx.amount) || 0;
          return sum + (isDebit ? -amt : amt);
        }, 0);
      }
    }

    return {
      appMining: Math.max(0, appMining),
      zetaBrowser: Math.max(0, zetaBrowser),
      eventPrizes: Math.max(0, eventPrizes),
      eventPoints: Math.max(0, eventPoints),
    };
  };

  const fetchOverallTotals = useCallback(async () => {
    if (!routeUserId) return;
    try {
      const res = await getUserCoinHistory(routeUserId, {
        page: 1,
        perPage: 1000,
        source: "all",
        type: "all",
      });

      if (res?.status && res?.data) {
        const summaryObj = res.data?.summary || {};
        const historyList = Array.isArray(res.data)
          ? res.data
          : Array.isArray(res.data?.history)
          ? res.data.history
          : [];

        const hasExplicitTotals =
          summaryObj.appMining != null ||
          summaryObj.app_mining != null ||
          summaryObj.miningTotal != null ||
          summaryObj.totalAppMining != null ||
          summaryObj.zetaBrowser != null ||
          summaryObj.zeta_browser != null ||
          summaryObj.browserTotal != null ||
          summaryObj.totalZetaBrowser != null ||
          summaryObj.eventPrizes != null ||
          summaryObj.event_prize != null ||
          summaryObj.eventPrizeTotal != null ||
          summaryObj.totalEventPrizes != null ||
          summaryObj.eventPoints != null ||
          summaryObj.event_point != null ||
          summaryObj.eventPointsTotal != null ||
          summaryObj.totalEventPoints != null;

        if (hasExplicitTotals) {
          setSourceTotals({
            appMining: Number(summaryObj.appMining ?? summaryObj.app_mining ?? summaryObj.totalAppMining ?? summaryObj.miningTotal ?? 0),
            zetaBrowser: Number(summaryObj.zetaBrowser ?? summaryObj.zeta_browser ?? summaryObj.totalZetaBrowser ?? summaryObj.browserTotal ?? 0),
            eventPrizes: Number(summaryObj.eventPrizes ?? summaryObj.event_prize ?? summaryObj.totalEventPrizes ?? summaryObj.eventPrizeTotal ?? 0),
            eventPoints: Number(summaryObj.eventPoints ?? summaryObj.event_point ?? summaryObj.event_points ?? summaryObj.totalEventPoints ?? summaryObj.eventPointsTotal ?? 0),
          });
        } else {
          setSourceTotals(computeSourceTotalsFromList(historyList));
        }
      }
    } catch (err) {
      console.error("Error fetching overall source totals:", err);
    }
  }, [routeUserId]);

  // Fetch basic user profile for header info
  const fetchUserProfile = useCallback(async () => {
    if (!routeUserId) return;
    try {
      const token = localStorage.getItem("token");
      const res = await fetch(`${baseUrl}/users/view/${routeUserId}`, {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      });
      const data = await res.json();
      if (data?.status && data?.data) {
        setUserInfo(data.data);
      }
    } catch {
      // ignore
    }
  }, [routeUserId]);

  const fetchHistory = useCallback(async (isRefresh = false) => {
    if (!routeUserId) return;
    if (isRefresh) {
      setRefreshing(true);
      fetchOverallTotals();
    } else {
      setLoading(true);
    }
    setError("");

    try {
      // When filtering by Reversions (ban), fetch all sources from backend so DEBIT & point reversions are included
      let apiSource = sourceFilter;
      if (sourceFilter === "ban") apiSource = "all";
      else if (sourceFilter === "mini_games" || sourceFilter === "game" || sourceFilter === "games") apiSource = "mini_games";
      else if (sourceFilter === "event_points" || sourceFilter === "points") apiSource = "event_points";

      const apiLimit = sourceFilter === "ban" ? 200 : perPage;

      const res = await getUserCoinHistory(routeUserId, {
        page,
        perPage: apiLimit,
        source: apiSource,
        type: typeFilter,
      });

      if (res?.status && res?.data) {
        const rawHistoryList = Array.isArray(res.data)
          ? res.data
          : Array.isArray(res.data?.history)
          ? res.data.history
          : [];

        // Check local deductions to ensure immediate display even before backend sync
        const localDeductions = getLocalPointDeductions();
        const localRevertTxs = [];
        Object.entries(localDeductions).forEach(([k, d]) => {
          if (
            (String(d.userId) === String(routeUserId) || k.endsWith(`_${routeUserId}`)) &&
            Number(d.revertedPoints) > 0
          ) {
            const hasMatchingBackendDebit = rawHistoryList.some(
              (tx) =>
                (String(tx.transactionType || tx.type).toUpperCase() === "DEBIT" ||
                  String(tx.source || "").includes("revert")) &&
                Number(tx.amount || 0) === Number(d.lastRevertAmount || d.revertedPoints)
            );
            if (!hasMatchingBackendDebit) {
              localRevertTxs.push({
                id: `local_rev_${d.eventId || "evt"}_${d.userId}_${d.updatedAt || Date.now()}`,
                userId: String(routeUserId),
                source: "admin_reversion",
                type: "DEBIT",
                transactionType: "DEBIT",
                amount: Number(d.lastRevertAmount || d.revertedPoints),
                details: `Event Point Reversion: ${d.reason || "Admin Manual Point Revert"}`,
                transactionDate: d.updatedAt || new Date().toISOString(),
                createdAt: d.updatedAt || new Date().toISOString(),
                status: "Reverted",
              });
            }
          }
        });

        const combinedHistory = [...localRevertTxs, ...rawHistoryList];

        // Apply strict source and type filtering on the list
        let filteredList = combinedHistory;
        if (sourceFilter && sourceFilter !== "all") {
          filteredList = filteredList.filter((tx) => {
            const s = String(tx.source || "").toLowerCase().trim();
            const isDebit = String(tx.transactionType || tx.type || "").toUpperCase() === "DEBIT" || Number(tx.amount || 0) < 0;
            const detailsText = String(tx.details || tx.reason || "").toLowerCase();
            const hasRevertWord = detailsText.includes("revert") || detailsText.includes("deduct") || detailsText.includes("penalty");

            if (sourceFilter === "mining") return s === "app_mining" || s === "mining";
            if (sourceFilter === "browser") return s === "zeta_browser" || s === "browser";
            if (sourceFilter === "event") return s === "event_prize" || s === "event_prizes" || s === "event";
            if (sourceFilter === "points") return s === "event_point" || s === "event_points" || s === "points";
            if (sourceFilter === "ban") {
              return (
                s === "admin_reversion" ||
                s === "ban" ||
                s === "reversion" ||
                s === "reversions" ||
                s === "event_reversion" ||
                s === "point_reversion" ||
                s.includes("revert") ||
                isDebit ||
                hasRevertWord
              );
            }
            return true;
          });
        }
        if (typeFilter && typeFilter !== "all") {
          filteredList = filteredList.filter(
            (tx) => String(tx.transactionType || tx.type || "").toUpperCase() === typeFilter.toUpperCase()
          );
        }

        setHistory(filteredList);


        // Derive user details from first transaction if profile not loaded
        if (rawHistoryList.length > 0 && !userInfo) {
          const first = rawHistoryList[0];
          setUserInfo((prev) => prev || {
            userId: first.userId || routeUserId,
            username: first.username,
            email: first.email,
          });
        }

        const computedSummary = res.data?.summary || {
          currentPageCredited: filteredList
            .filter((h) => (h.transactionType || "").toUpperCase() === "CREDIT")
            .reduce((acc, h) => acc + (Number(h.amount) || 0), 0),
          currentPageDebited: filteredList
            .filter((h) => (h.transactionType || "").toUpperCase() === "DEBIT")
            .reduce((acc, h) => acc + (Number(h.amount) || 0), 0),
        };
        setSummary(computedSummary);

        // If backend summary contains source breakdown, update source totals
        if (
          computedSummary.appMining != null ||
          computedSummary.app_mining != null ||
          computedSummary.zetaBrowser != null ||
          computedSummary.zeta_browser != null ||
          computedSummary.eventPrizes != null ||
          computedSummary.event_prize != null ||
          computedSummary.eventPoints != null ||
          computedSummary.event_point != null
        ) {
          setSourceTotals({
            appMining: Number(computedSummary.appMining ?? computedSummary.app_mining ?? computedSummary.totalAppMining ?? computedSummary.miningTotal ?? 0),
            zetaBrowser: Number(computedSummary.zetaBrowser ?? computedSummary.zeta_browser ?? computedSummary.totalZetaBrowser ?? computedSummary.browserTotal ?? 0),
            eventPrizes: Number(computedSummary.eventPrizes ?? computedSummary.event_prize ?? computedSummary.totalEventPrizes ?? computedSummary.eventPrizeTotal ?? 0),
            eventPoints: Number(computedSummary.eventPoints ?? computedSummary.event_point ?? computedSummary.event_points ?? computedSummary.totalEventPoints ?? computedSummary.eventPointsTotal ?? 0),
          });
        } else if (sourceFilter === "all" && typeFilter === "all" && rawHistoryList.length > 0 && (res.data?.pagination?.total || rawHistoryList.length) <= rawHistoryList.length) {
          setSourceTotals(computeSourceTotalsFromList(rawHistoryList));
        }

        const serverPagination = res.data?.pagination || res.pagination || {};
        const totalRecords =
          typeof serverPagination?.total === "number"
            ? serverPagination.total
            : typeof res.data?.total === "number"
            ? res.data.total
            : typeof res.total === "number"
            ? res.total
            : filteredList.length;

        const totalPagesCount =
          typeof serverPagination?.totalPages === "number"
            ? serverPagination.totalPages
            : typeof serverPagination?.lastPage === "number"
            ? serverPagination.lastPage
            : Math.max(1, Math.ceil(totalRecords / perPage));

        const currentPageNum =
          typeof serverPagination?.page === "number"
            ? serverPagination.page
            : typeof serverPagination?.currentPage === "number"
            ? serverPagination.currentPage
            : page;

        setPagination({
          total: totalRecords,
          totalPages: totalPagesCount,
          page: currentPageNum,
          perPage,
        });
      } else {
        throw new Error(res?.message || "Failed to load coin history");
      }
    } catch (err) {
      console.error("Error loading user coin history:", err);
      setError(err.message || "Failed to load user coin history");
      setHistory([]);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [routeUserId, page, perPage, sourceFilter, typeFilter, userInfo, fetchOverallTotals]);

  useEffect(() => {
    fetchUserProfile();
    fetchOverallTotals();
  }, [fetchUserProfile, fetchOverallTotals]);

  useEffect(() => {
    fetchHistory(false);
  }, [fetchHistory]);

  const copyCode = (code) => {
    if (!code) return;
    navigator.clipboard.writeText(code);
    setCopied(true);
    toast.success("Copied to clipboard!");
    setTimeout(() => setCopied(false), 2000);
  };

  const displayName = userInfo?.username || `User #${routeUserId}`;
  const displayEmail = userInfo?.email || "";
  const displayCoins = userInfo?.coin != null ? parseFloat(userInfo.coin).toFixed(4) : null;
  const avatarLetter = (displayName || "U")[0].toUpperCase();

  return (
    <div className="flex bg-gray-100 min-h-screen">
      <Toaster position="top-right" />
      <Sidebar />
      <div className="flex-1 flex flex-col min-w-0">
        <Navbar />

        <div className="p-5 sm:p-8 space-y-6 w-full">
          {/* Breadcrumbs & Smart Back Navigation Bar */}
          <div className="space-y-3">
            <div className="flex items-center flex-wrap gap-1.5 text-xs text-gray-500 font-medium">
              <Link
                to="/user"
                className="hover:text-black hover:underline transition flex items-center gap-1"
              >
                Users
              </Link>
              <FiChevronRight size={12} className="text-gray-400 flex-shrink-0" />
              <Link
                to={`/user/${routeUserId}`}
                className="hover:text-black hover:underline transition font-semibold"
                title="View User Details"
              >
                {userInfo?.username ? `${userInfo.username} (#${routeUserId})` : `User #${routeUserId}`}
              </Link>
              <FiChevronRight size={12} className="text-gray-400 flex-shrink-0" />
              <span className="text-gray-900 font-bold bg-[#ffffcc] px-2 py-0.5 rounded">
                Coin History Ledger
              </span>
            </div>

            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
              <div className="flex items-center gap-3">
                <button
                  type="button"
                  onClick={() => {
                    if (window.history.length > 1) {
                      navigate(-1);
                    } else {
                      navigate(`/user/${routeUserId}`);
                    }
                  }}
                  className="p-2.5 bg-white hover:bg-gray-50 border border-gray-200 rounded-xl text-gray-700 hover:text-black transition shadow-xs cursor-pointer flex items-center justify-center"
                  title="Back to Previous Page"
                  aria-label="Back"
                >
                  <FiArrowLeft size={18} />
                </button>
                <div>
                  <h1 className="text-2xl font-black text-gray-900 tracking-tight">
                    Coin History Ledger
                  </h1>
                  <p className="text-xs text-gray-400 mt-0.5">
                    Unified audit ledger of all coin credits, debits, and balance changes
                  </p>
                </div>
              </div>

              <div className="flex items-center gap-2 flex-wrap">
                <button
                  type="button"
                  onClick={handleOpenRevertModal}
                  className="flex items-center gap-1.5 text-xs font-bold text-rose-700 bg-rose-50 hover:bg-rose-100 border border-rose-200 px-3.5 py-2 rounded-xl transition shadow-2xs cursor-pointer"
                  title="Revert / Deduct Event Points"
                >
                  <FiRotateCcw size={13} className="text-rose-600" />
                  <span>Revert Points</span>
                </button>

                <button
                  onClick={() => fetchHistory(true)}
                  disabled={refreshing || loading}
                  className="flex items-center gap-1.5 text-xs font-bold text-gray-700 bg-white hover:bg-gray-50 border border-gray-200 px-3.5 py-2 rounded-xl transition shadow-2xs cursor-pointer"
                >
                  <FiRefreshCw
                    size={13}
                    className={refreshing ? "animate-spin text-[#999900]" : ""}
                  />
                  <span>Refresh</span>
                </button>

                <Link
                  to={`/user/${routeUserId}`}
                  className="flex items-center gap-1.5 text-xs font-bold text-black bg-[#ffff00] hover:bg-[#e6e600] border border-[#ffff00] px-3.5 py-2 rounded-xl transition shadow-2xs"
                >
                  <FiExternalLink size={13} /> View Profile
                </Link>
              </div>
            </div>
          </div>

          {/* User Profile Header Card */}
          <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6">
            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-5">
              <div className="flex items-start gap-4">
                <div className="w-14 h-14 rounded-2xl bg-[#3b82f6] text-white flex items-center justify-center text-2xl font-bold flex-shrink-0 shadow-xs">
                  {avatarLetter}
                </div>

                <div className="min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <h2 className="text-xl font-black text-gray-900">
                      {displayName}
                    </h2>
                    <span className="text-xs font-mono font-bold px-2.5 py-0.5 rounded-md bg-gray-100 text-gray-700 border border-gray-200">
                      ID: #{routeUserId}
                    </span>
                    {userInfo?.isMiningActive && (
                      <span className="px-2.5 py-0.5 rounded-full text-xs font-semibold bg-emerald-50 text-emerald-700 border border-emerald-200">
                        Mining Active
                      </span>
                    )}
                  </div>
                  {displayEmail && (
                    <p className="text-xs text-gray-500 mt-1">{displayEmail}</p>
                  )}
                  {userInfo?.referralCode && (
                    <div className="flex items-center gap-1.5 mt-1.5">
                      <span className="text-xs font-mono text-gray-400">
                        Referral Code: <strong className="text-gray-700">{userInfo.referralCode}</strong>
                      </span>
                      <button
                        onClick={() => copyCode(userInfo.referralCode)}
                        className="text-gray-400 hover:text-gray-600"
                        title="Copy code"
                      >
                        {copied ? (
                          <FiCheck size={12} className="text-green-600" />
                        ) : (
                          <FiCopy size={12} />
                        )}
                      </button>
                    </div>
                  )}
                </div>
              </div>

              <div className="flex flex-row md:flex-col items-center md:items-end justify-between border-t md:border-t-0 pt-3 md:pt-0 border-gray-100 gap-1">
                <p className="text-xs text-gray-400 font-semibold uppercase tracking-wider">
                  Current Balance
                </p>
                <p className="text-2xl font-black text-blue-600 font-mono">
                  {displayCoins !== null ? `${displayCoins} ZX` : "—"}
                </p>
                <p className="text-[11px] text-gray-400">
                  Total audited transactions: {pagination.total}
                </p>
              </div>
            </div>
          </div>

          {/* Quick Metrics Bar: 4 Source Breakdown Cards */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {/* 1. Total of App Mining */}
            <div className="bg-white rounded-2xl shadow-sm border border-emerald-100 p-4 flex items-center justify-between">
              <div>
                <p className="text-xs text-emerald-700 font-semibold uppercase tracking-wider flex items-center gap-1">
                  ⚡ Total of App Mining
                </p>
                <p className="text-2xl font-black text-emerald-600 font-mono mt-0.5">
                  {formatCoinAmount(sourceTotals.appMining)} ZX
                </p>
              </div>
              <div className="w-10 h-10 rounded-xl bg-emerald-50 text-emerald-700 flex items-center justify-center text-xl shadow-2xs">
                ⚡
              </div>
            </div>

            {/* 2. Zeta Browser Total */}
            <div className="bg-white rounded-2xl shadow-sm border border-blue-100 p-4 flex items-center justify-between">
              <div>
                <p className="text-xs text-blue-700 font-semibold uppercase tracking-wider flex items-center gap-1">
                  💻 Zeta Browser Total
                </p>
                <p className="text-2xl font-black text-blue-600 font-mono mt-0.5">
                  {formatCoinAmount(sourceTotals.zetaBrowser)} ZX
                </p>
              </div>
              <div className="w-10 h-10 rounded-xl bg-blue-50 text-blue-700 flex items-center justify-center text-xl shadow-2xs">
                💻
              </div>
            </div>

            {/* 3. Event Prizes */}
            <div className="bg-white rounded-2xl shadow-sm border border-amber-100 p-4 flex items-center justify-between">
              <div>
                <p className="text-xs text-amber-800 font-semibold uppercase tracking-wider flex items-center gap-1">
                  🏆 Event Prizes
                </p>
                <p className="text-2xl font-black text-amber-600 font-mono mt-0.5">
                  {formatCoinAmount(sourceTotals.eventPrizes)} ZX
                </p>
              </div>
              <div className="w-10 h-10 rounded-xl bg-amber-50 text-amber-800 flex items-center justify-center text-xl shadow-2xs">
                🏆
              </div>
            </div>

            {/* 4. Event Points */}
            <div className="bg-white rounded-2xl shadow-sm border border-purple-100 p-4 flex items-center justify-between">
              <div>
                <p className="text-xs text-purple-700 font-semibold uppercase tracking-wider flex items-center gap-1">
                  🎯 Event Points
                </p>
                <p className="text-2xl font-black text-purple-600 font-mono mt-0.5">
                  {formatCoinAmount(sourceTotals.eventPoints)} PTS
                </p>
                <button
                  type="button"
                  onClick={handleOpenRevertModal}
                  className="inline-flex items-center gap-1 text-[11px] font-bold text-rose-700 bg-rose-50 hover:bg-rose-100 border border-rose-200 px-2 py-0.5 rounded-md mt-1.5 transition cursor-pointer shadow-2xs"
                  title="Revert / Deduct Event Points"
                >
                  <FiRotateCcw size={10} className="text-rose-600" />
                  <span>Revert Points</span>
                </button>
              </div>
              <div className="w-10 h-10 rounded-xl bg-purple-50 text-purple-700 flex items-center justify-center text-xl shadow-2xs">
                🎯
              </div>
            </div>
          </div>


          {/* Filter Toolbar */}
          <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-4 space-y-3">
            <div className="flex flex-col md:flex-row md:items-center justify-between gap-3">
              {/* Source Filters */}
              <div className="flex items-center gap-1.5 flex-wrap">
                <span className="text-xs font-bold text-gray-400 uppercase tracking-wider mr-1">Source:</span>
                {SOURCE_FILTERS.map((tab) => {
                  const isActive = sourceFilter === tab.key;
                  return (
                    <button
                      key={tab.key}
                      type="button"
                      onClick={() => {
                        setSourceFilter(tab.key);
                        setPage(1);
                      }}
                      className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-xl text-xs font-bold transition shadow-2xs ${
                        isActive
                          ? "bg-gray-900 text-[#ffff00] shadow-xs"
                          : "bg-white text-gray-600 hover:bg-gray-100 hover:text-gray-900 border border-gray-200"
                      }`}
                    >
                      <span>{tab.icon}</span>
                      <span>{tab.label}</span>
                    </button>
                  );
                })}
              </div>

              {/* Type Filters */}
              <div className="flex items-center gap-2">
                <span className="text-xs font-bold text-gray-400 uppercase tracking-wider">Type:</span>
                <div className="inline-flex bg-gray-100 rounded-xl p-1 border border-gray-200 shadow-2xs text-xs font-semibold">
                  {TYPE_FILTERS.map((t) => (
                    <button
                      key={t.key}
                      type="button"
                      onClick={() => {
                        setTypeFilter(t.key);
                        setPage(1);
                      }}
                      className={`px-3 py-1 rounded-lg transition ${
                        typeFilter === t.key
                          ? "bg-[#ffff00] text-black font-bold shadow-2xs"
                          : "text-gray-600 hover:text-gray-900"
                      }`}
                    >
                      {t.label}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          </div>

          {/* Transactions Table Card */}
          <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
            <div className="px-6 py-4 border-b border-gray-100 flex items-center justify-between">
              <h3 className="text-sm font-bold text-gray-800 flex items-center gap-2">
                <span>Coin History Audit Ledger</span>
              </h3>
              <p className="text-xs text-gray-400">
                Page {pagination.page || page} of {pagination.totalPages || 1} ({pagination.total} records)
              </p>
            </div>

            {loading ? (
              <div className="py-24 flex flex-col items-center justify-center gap-3 text-gray-400">
                <div className="w-8 h-8 border-4 border-[#ffff00] border-t-transparent rounded-full animate-spin" />
                <p className="text-xs font-semibold text-gray-500">Loading coin transactions...</p>
              </div>
            ) : error ? (
              <div className="p-8 text-center bg-red-50 text-red-600 text-sm">
                <div className="inline-flex p-3 rounded-full bg-red-100 text-red-600 mb-2">
                  <FiAlertTriangle size={24} />
                </div>
                <p className="font-bold">{error}</p>
                <button
                  onClick={() => fetchHistory(false)}
                  className="mt-3 px-4 py-1.5 text-xs font-bold bg-gray-900 text-white rounded-lg hover:bg-black transition"
                >
                  Retry
                </button>
              </div>
            ) : history.length === 0 ? (
              <div className="py-24 text-center text-gray-400 flex flex-col items-center justify-center gap-2">
                <div className="w-12 h-12 rounded-2xl bg-gray-100 text-gray-400 flex items-center justify-center text-2xl">
                  📂
                </div>
                <p className="text-sm font-semibold text-gray-700">No coin transactions found</p>
                <p className="text-xs text-gray-400">No activity for the selected filters.</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-left border-collapse text-xs">
                  <thead>
                    <tr className="bg-gray-50 border-b border-gray-200/80 text-[11px] font-bold text-gray-500 uppercase tracking-wider">
                      <th className="px-5 py-3.5">Date & Time</th>
                      <th className="px-5 py-3.5">Source</th>
                      <th className="px-5 py-3.5">Type</th>
                      <th className="px-5 py-3.5 text-right">Amount</th>
                      <th className="px-5 py-3.5 text-right">Balance After</th>
                      <th className="px-5 py-3.5 text-center">Status</th>
                      <th className="px-5 py-3.5">Details / Audit Note</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {history.map((tx, idx) => {
                      const isCredit = (tx.transactionType || "").toUpperCase() === "CREDIT";
                      const badgeStyle = getSourceBadgeStyle(tx.source);
                      const sourceIcon = getSourceIcon(tx.source);
                      const isPoints =
                        tx.source === "event_point" ||
                        tx.source === "event_points" ||
                        tx.source === "points" ||
                        (tx.unit || "").toUpperCase() === "PTS";
                      const currencyUnit = isPoints ? "PTS" : (tx.unit || "ZX");

                      return (
                        <tr
                          key={tx.id || `tx-${idx}`}
                          className="hover:bg-amber-50/20 transition-colors"
                        >
                          {/* Date */}
                          <td className="px-5 py-4 font-mono text-gray-600 whitespace-nowrap">
                            <div className="flex items-center gap-1.5">
                              <FiCalendar size={12} className="text-gray-400" />
                              <span>{formatDateTime(tx.transactionDate || tx.createdAt)}</span>
                            </div>
                          </td>

                          {/* Source */}
                          <td className="px-5 py-4 whitespace-nowrap">
                            <span
                              className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-lg text-xs font-bold border ${badgeStyle}`}
                            >
                              <span>{sourceIcon}</span>
                              <span>{tx.sourceLabel || tx.source || "System"}</span>
                            </span>
                          </td>

                          {/* Type */}
                          <td className="px-5 py-4 whitespace-nowrap">
                            <span
                              className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-[11px] font-black uppercase tracking-wide ${
                                isCredit
                                  ? "bg-emerald-100/80 text-emerald-800"
                                  : "bg-rose-100/80 text-rose-800"
                              }`}
                            >
                              {isCredit ? "+ CREDIT" : "- DEBIT"}
                            </span>
                          </td>

                          {/* Amount */}
                          <td className="px-5 py-4 text-right font-mono font-bold whitespace-nowrap">
                            <span
                              className={`text-sm ${
                                isCredit ? "text-emerald-600" : "text-rose-600"
                              }`}
                            >
                              {isCredit ? "+" : "-"}
                              {formatCoinAmount(tx.amount)} {currencyUnit}
                            </span>
                          </td>

                          {/* Balance After */}
                          <td className="px-5 py-4 text-right font-mono text-gray-700 whitespace-nowrap">
                            {tx.balanceAfter != null ? (
                              <span className="font-semibold">{formatCoinAmount(tx.balanceAfter)} {currencyUnit}</span>
                            ) : (
                              <span className="text-gray-400">—</span>
                            )}
                          </td>

                          {/* Status */}
                          <td className="px-5 py-4 text-center whitespace-nowrap">
                            <span className="inline-block px-2.5 py-0.5 rounded-full text-[11px] font-bold bg-gray-100 text-gray-700 border border-gray-200">
                              {tx.status || "Completed"}
                            </span>
                          </td>

                          {/* Details */}
                          <td className="px-5 py-4 text-gray-600 max-w-sm">
                            {(() => {
                              const srcLower = String(tx.source || "").toLowerCase();
                              const detailsStr = String(tx.details || "");
                              const is2048 = srcLower.includes("2048") || detailsStr.includes("2048");
                              const isFruit = srcLower.includes("fruit") || detailsStr.toLowerCase().includes("fruit");

                              if (is2048) {
                                return (
                                  <div className="flex items-center gap-1.5 flex-wrap">
                                    <span className="inline-flex items-center gap-1 font-bold text-amber-800 bg-amber-50 px-2 py-0.5 rounded border border-amber-200">
                                      <span>🧩</span>
                                      <span>{detailsStr || `2048 Game | Event: ${tx.eventTitle || "Zeta Rush"}`}</span>
                                    </span>
                                  </div>
                                );
                              }
                              if (isFruit) {
                                return (
                                  <div className="flex items-center gap-1.5 flex-wrap">
                                    <span className="inline-flex items-center gap-1 font-bold text-rose-800 bg-rose-50 px-2 py-0.5 rounded border border-rose-200">
                                      <span>🍉</span>
                                      <span>{detailsStr || `Fruit Bomb Game | Event: ${tx.eventTitle || "Zeta Rush"}`}</span>
                                    </span>
                                  </div>
                                );
                              }

                              return (
                                <span className="line-clamp-2" title={detailsStr || ""}>
                                  {detailsStr || "—"}
                                </span>
                              );
                            })()}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}

            {/* Pagination Controls */}
            {history.length > 0 && (
              <div className="px-6 py-4 bg-gray-50 border-t border-gray-100 flex items-center justify-between text-xs text-gray-500 flex-wrap gap-3">
                <div className="flex items-center gap-3">
                  <span>
                    Showing{" "}
                    <strong>
                      {history.length > 0 ? (page - 1) * perPage + 1 : 0}
                    </strong>{" "}
                    to{" "}
                    <strong>
                      {Math.min(page * perPage, pagination.total || history.length)}
                    </strong>{" "}
                    of <strong>{pagination.total || history.length}</strong> transactions
                  </span>
                  <div className="flex items-center gap-1.5 ml-2">
                    <span className="text-gray-400 font-medium">Per page:</span>
                    <select
                      value={perPage}
                      onChange={(e) => {
                        setPerPage(Number(e.target.value));
                        setPage(1);
                      }}
                      className="bg-white border border-gray-200 rounded-lg px-2 py-1 text-xs font-semibold text-gray-700 outline-hidden hover:border-gray-300 focus:border-yellow-400 cursor-pointer shadow-2xs"
                    >
                      <option value={20}>20</option>
                      <option value={50}>50</option>
                      <option value={100}>100</option>
                      <option value={200}>200</option>
                    </select>
                  </div>
                </div>

                <div className="flex items-center gap-1">
                  <button
                    disabled={page <= 1 || loading}
                    onClick={() => setPage((p) => Math.max(1, p - 1))}
                    className="px-3 py-1.5 rounded-lg border border-gray-200 bg-white hover:bg-gray-100 disabled:opacity-40 disabled:cursor-not-allowed font-medium transition shadow-2xs"
                  >
                    Previous
                  </button>
                  <span className="px-3 py-1.5 text-xs font-bold text-gray-700 bg-white border border-gray-200 rounded-lg shadow-2xs">
                    Page {pagination.page || page} of {pagination.totalPages || 1}
                  </span>
                  <button
                    disabled={page >= (pagination.totalPages || 1) || loading}
                    onClick={() => setPage((p) => Math.min(pagination.totalPages || 1, p + 1))}
                    className="px-3 py-1.5 rounded-lg border border-gray-200 bg-white hover:bg-gray-100 disabled:opacity-40 disabled:cursor-not-allowed font-medium transition shadow-2xs"
                  >
                    Next
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Revert / Deduct Event Points Modal */}
      {revertModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-xs overflow-y-auto">
          <div className="bg-white rounded-2xl shadow-2xl border border-gray-100 w-full max-w-lg overflow-hidden animate-in fade-in zoom-in-95 duration-150 my-auto">
            {/* Modal Header */}
            <div className="bg-gradient-to-r from-rose-50 via-red-50 to-amber-50 border-b border-rose-100 p-5 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-rose-100 text-rose-700 flex items-center justify-center font-bold shadow-xs">
                  <FiRotateCcw size={20} />
                </div>
                <div>
                  <h3 className="text-base font-black text-gray-900">
                    Revert Event Points
                  </h3>
                  <p className="text-xs text-gray-500 mt-0.5">
                    Deduct or revert custom event points for this user
                  </p>
                </div>
              </div>
              <button
                type="button"
                onClick={handleCloseRevertModal}
                disabled={revertLoading}
                className="p-1.5 text-gray-400 hover:text-gray-700 hover:bg-white/80 rounded-lg transition cursor-pointer"
              >
                <FiX size={18} />
              </button>
            </div>

            {/* Modal Body */}
            <div className="p-6 space-y-4">
              {/* User Profile & Score Summary */}
              <div className="bg-gray-50 border border-gray-200/80 rounded-xl p-3.5 flex items-center justify-between gap-3">
                <div className="flex items-center gap-3 min-w-0">
                  <div className="w-10 h-10 rounded-full bg-blue-100 text-blue-700 flex items-center justify-center font-bold text-sm flex-shrink-0 shadow-2xs">
                    {avatarLetter}
                  </div>
                  <div className="min-w-0">
                    <span className="font-bold text-gray-900 block truncate text-xs">
                      {displayName}
                    </span>
                    <div className="flex items-center gap-1.5 text-[11px] text-gray-500">
                      <span className="truncate max-w-[140px]">{displayEmail || "No email"}</span>
                      <span className="font-mono text-gray-600 bg-white px-1 py-0.5 rounded border border-gray-200 text-[10px]">
                        #{routeUserId}
                      </span>
                    </div>
                  </div>
                </div>

                <div className="text-right flex-shrink-0">
                  <span className="text-[10px] uppercase font-bold text-gray-400 block">
                    Event Points Total
                  </span>
                  <span className="inline-flex items-center gap-1 font-black text-xs bg-purple-100 text-purple-900 px-2 py-0.5 rounded-md mt-0.5">
                    🎯 {formatCoinAmount(sourceTotals.eventPoints)} PTS
                  </span>
                </div>
              </div>

              {/* Event Selector (if multiple events exist) */}
              {eventsList.length > 0 && (
                <div className="space-y-1">
                  <label className="text-xs font-bold text-gray-700 block">
                    Select Event
                  </label>
                  <select
                    value={selectedEventId}
                    onChange={(e) => setSelectedEventId(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-200 rounded-xl text-xs font-semibold text-gray-800 focus:outline-none focus:ring-2 focus:ring-[#ffff00] bg-white cursor-pointer"
                  >
                    {eventsList.map((evt) => (
                      <option key={evt.eventId || evt.id} value={evt.eventId || evt.id}>
                        {evt.eventName} ({evt.status})
                      </option>
                    ))}
                  </select>
                </div>
              )}

              {/* Point Amount Input & Presets */}
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <label className="text-xs font-bold text-gray-700 flex items-center gap-1.5">
                    <span>Points to Revert / Deduct</span>
                    <span className="text-rose-500">*</span>
                  </label>
                  <span className="text-[11px] text-gray-400">
                    Max: {sourceTotals.eventPoints.toLocaleString()} pts
                  </span>
                </div>

                <div className="relative">
                  <span className="absolute left-3.5 top-1/2 -translate-y-1/2 text-gray-400 font-bold text-sm">
                    🎯
                  </span>
                  <input
                    type="number"
                    min="1"
                    max={sourceTotals.eventPoints > 0 ? sourceTotals.eventPoints : undefined}
                    step="1"
                    placeholder="e.g. 50"
                    value={revertAmount}
                    onChange={(e) => {
                      setRevertAmount(e.target.value);
                      setRevertPreset("custom");
                    }}
                    className="w-full pl-9 pr-20 py-2.5 border border-gray-200 rounded-xl text-sm font-bold text-gray-900 focus:outline-none focus:ring-2 focus:ring-[#ffff00] focus:border-black"
                  />
                  <span className="absolute right-3.5 top-1/2 -translate-y-1/2 text-xs font-semibold text-gray-400">
                    Points
                  </span>
                </div>

                {/* Quick Presets */}
                {sourceTotals.eventPoints > 0 && (
                  <div className="flex items-center gap-1.5 pt-1">
                    <span className="text-[11px] font-semibold text-gray-500 mr-1">Quick:</span>
                    {[
                      { label: "25%", val: 25 },
                      { label: "50%", val: 50 },
                      { label: "75%", val: 75 },
                      { label: "100% (All)", val: 100 },
                    ].map((p) => (
                      <button
                        key={p.val}
                        type="button"
                        onClick={() => handlePresetSelect(p.val)}
                        className={`px-2.5 py-1 rounded-lg text-xs font-bold transition border cursor-pointer ${
                          revertPreset === String(p.val)
                            ? "bg-rose-100 text-rose-800 border-rose-300 shadow-2xs"
                            : "bg-gray-50 hover:bg-gray-100 text-gray-700 border-gray-200"
                        }`}
                      >
                        {p.label}
                      </button>
                    ))}
                  </div>
                )}
              </div>

              {/* Real-time Calculation Breakdown Preview */}
              <div className="bg-rose-50/50 border border-rose-100 rounded-xl p-3 space-y-1.5 text-xs">
                <div className="flex justify-between items-center text-gray-600">
                  <span>Current Event Points:</span>
                  <span className="font-bold text-gray-800">
                    🎯 {sourceTotals.eventPoints.toLocaleString()} PTS
                  </span>
                </div>
                <div className="flex justify-between items-center text-rose-600 font-semibold">
                  <span>Revert / Deduction:</span>
                  <span>- 🎯 {(Number(revertAmount) || 0).toLocaleString()} PTS</span>
                </div>
                <div className="border-t border-rose-200/60 pt-1.5 flex justify-between items-center font-black text-gray-900">
                  <span>New Balance After Revert:</span>
                  <span className="text-emerald-700 font-extrabold bg-emerald-50 px-2 py-0.5 rounded border border-emerald-200">
                    🎯 {Math.max(0, sourceTotals.eventPoints - (Number(revertAmount) || 0)).toLocaleString()} PTS
                  </span>
                </div>
              </div>

              {/* Reason Selector */}
              <div className="space-y-2">
                <label className="text-xs font-bold text-gray-700 block">
                  Revert Reason
                </label>
                <select
                  value={revertReason}
                  onChange={(e) => setRevertReason(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-200 rounded-xl text-xs font-semibold text-gray-800 focus:outline-none focus:ring-2 focus:ring-[#ffff00] bg-white cursor-pointer"
                >
                  {REVERT_REASON_OPTIONS.map((opt) => (
                    <option key={opt} value={opt}>
                      {opt}
                    </option>
                  ))}
                </select>

                <input
                  type="text"
                  placeholder="Optional custom admin note (e.g. Ticket #4829 or specific check-in date)..."
                  value={revertCustomNote}
                  onChange={(e) => setRevertCustomNote(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-200 rounded-xl text-xs text-gray-800 focus:outline-none focus:ring-2 focus:ring-[#ffff00]"
                />
              </div>

              {/* Warning Notice */}
              <div className="flex items-start gap-2 bg-amber-50 border border-amber-200 rounded-xl p-3 text-xs text-amber-900">
                <FiAlertTriangle className="text-amber-600 flex-shrink-0 mt-0.5" size={15} />
                <span>
                  <strong>Notice:</strong> This action will deduct <strong>{(Number(revertAmount) || 0).toLocaleString()} points</strong> and record a <strong>DEBIT</strong> transaction in the ledger.
                </span>
              </div>
            </div>

            {/* Modal Footer */}
            <div className="bg-gray-50 border-t border-gray-100 px-6 py-4 flex items-center justify-end gap-2.5">
              <button
                type="button"
                onClick={handleCloseRevertModal}
                disabled={revertLoading}
                className="px-4 py-2 border border-gray-200 rounded-xl text-xs font-bold text-gray-700 hover:bg-gray-100 transition cursor-pointer"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleConfirmRevert}
                disabled={
                  revertLoading ||
                  !Number(revertAmount) ||
                  Number(revertAmount) <= 0
                }
                className="flex items-center gap-1.5 px-4 py-2 bg-rose-600 hover:bg-rose-700 disabled:opacity-50 disabled:cursor-not-allowed text-white rounded-xl text-xs font-bold shadow-xs transition cursor-pointer"
              >
                {revertLoading ? (
                  <>
                    <div className="w-3.5 h-3.5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                    <span>Reverting Points...</span>
                  </>
                ) : (
                  <>
                    <FiRotateCcw size={13} />
                    <span>Confirm Revert ({(Number(revertAmount) || 0).toLocaleString()} Pts)</span>
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}


